import { AdminTwoPanelLayout } from '@/components/admin/AdminTwoPanelLayout';
import { CopyProviderRulesDialog } from '@/components/admin/CopyProviderRulesDialog';
import { EditProviderRuleDialog } from '@/components/admin/EditProviderRuleDialog';
import { ProviderListSidebar } from '@/components/admin/ProviderListSidebar';
import { ProviderRulesPanel } from '@/components/admin/ProviderRulesPanel';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { trpc } from '@/utils/trpc';
import { useSuperAdmin } from '@/utils/use-super-admin';
import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Copy, Loader2, Plus, ShieldCheck, User } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_protected/admin/provider-rules')({
  component: ProviderRulesPage,
});

function ProviderRulesPage() {
  const { isSuperAdmin, isLoading: isAdminLoading } = useSuperAdmin();
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const router = useRouter();

  // Use current organization ID from Clerk
  const currentOrganizationId = organization?.id;

  // Redirect non-super admins to dashboard
  useEffect(() => {
    if (!isAdminLoading && !isSuperAdmin) {
      router.navigate({ to: '/dashboard' });
    }
  }, [isAdminLoading, isSuperAdmin, router]);

  // Configuration
  const selectedEhrSystem = 'imedicware' as const;

  // State
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [showAddRuleDialog, setShowAddRuleDialog] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [deletingRuleId, setDeletingRuleId] = useState<string | null>(null);

  // Queries
  const providersQuery = trpc.admin.getProvidersWithRules.useQuery(
    { organizationId: currentOrganizationId || '', ehrSystem: selectedEhrSystem },
    { enabled: !!currentOrganizationId }
  );

  const rulesQuery = trpc.admin.getProviderRules.useQuery(
    { organizationId: currentOrganizationId || '', ehrSystem: selectedEhrSystem },
    { enabled: !!currentOrganizationId }
  );

  // Mutations
  const deleteMutation = trpc.admin.deleteProviderRule.useMutation({
    onSuccess: () => {
      toast.success('Rule deleted successfully');
      rulesQuery.refetch();
      providersQuery.refetch();
      setDeletingRuleId(null);
    },
    onError: (error) => {
      toast.error(`Failed to delete rule: ${error.message}`);
    },
  });

  // Filter rules for selected provider
  const selectedProviderRules = useMemo(() => {
    if (!selectedProviderId || !rulesQuery.data?.rules) return [];
    return rulesQuery.data.rules.filter((rule) => rule.providerId === selectedProviderId) as Array<{
      id: string;
      providerId: string;
      providerName: string;
      ruleLabel: string;
      ruleType: 'ALLOW' | 'DENY';
      entityType: 'CONDITION' | 'INTENT' | 'PROCEDURE';
      canonicalTag: string;
      canonicalTagLabel: string;
      canonicalTagDescription?: string | null;
    }>;
  }, [selectedProviderId, rulesQuery.data?.rules]);

  // Get selected provider info
  const selectedProvider = useMemo(() => {
    if (!selectedProviderId || !providersQuery.data?.providers) return null;
    return providersQuery.data.providers.find((p) => p.id === selectedProviderId) || null;
  }, [selectedProviderId, providersQuery.data?.providers]);

  // Get rule being edited
  const editingRule = useMemo(() => {
    if (!editingRuleId || !rulesQuery.data?.rules) return null;
    const rule = rulesQuery.data.rules.find((r) => r.id === editingRuleId);
    if (!rule) return null;
    return {
      ...rule,
      ruleType: rule.ruleType as 'ALLOW' | 'DENY',
      entityType: rule.entityType as 'CONDITION' | 'INTENT' | 'PROCEDURE',
    };
  }, [editingRuleId, rulesQuery.data?.rules]);

  // Handlers
  const handleRefresh = () => {
    rulesQuery.refetch();
    providersQuery.refetch();
  };

  const handleAddRule = () => {
    setShowAddRuleDialog(true);
  };

  const handleEditRule = (ruleId: string) => {
    setEditingRuleId(ruleId);
  };

  const handleDeleteRule = (ruleId: string) => {
    setDeletingRuleId(ruleId);
  };

  const handleConfirmDelete = () => {
    if (deletingRuleId && currentOrganizationId) {
      deleteMutation.mutate({ organizationId: currentOrganizationId, ruleId: deletingRuleId });
    }
  };

  const handleCopyRules = () => {
    setShowCopyDialog(true);
  };

  const handleCopySuccess = () => {
    handleRefresh();
  };

  // Loading state
  if (isAdminLoading || !orgLoaded) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  // Access denied
  if (!isSuperAdmin) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-4 p-4">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <User className="h-5 w-5" />
                Access Denied
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Provider Qualification Rules is only available to super administrators. Please
                contact your administrator if you need access.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <AdminTwoPanelLayout
      title="Provider Qualification Rules"
      description="Configure which patients providers can see based on conditions, intents, and procedures"
      icon={ShieldCheck}
      isSuperAdminPage={true}
      sidebarWidth="narrow"
      sidebarContent={
        currentOrganizationId && (
          <ProviderListSidebar
            providers={
              providersQuery.data?.providers.map((p) => ({
                id: p.id,
                name: p.name,
                ehrId: p.ehrId,
                count: p.ruleCount,
                countLabel: 'rule',
              })) || []
            }
            selectedProviderId={selectedProviderId}
            onSelectProvider={setSelectedProviderId}
            isLoading={providersQuery.isLoading}
          />
        )
      }
      mainContent={
        !currentOrganizationId ? (
          <Card>
            <CardContent className="py-8">
              <p className="text-center text-muted-foreground">
                Please select an organization to manage provider qualification rules.
              </p>
            </CardContent>
          </Card>
        ) : !selectedProviderId ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center text-muted-foreground">
                <User className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                Select a provider to configure their qualification rules
              </div>
            </CardContent>
          </Card>
        ) : rulesQuery.isLoading ? (
          <Card>
            <CardContent className="py-12">
              <div className="flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Provider Header */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    {selectedProvider?.name}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button onClick={handleCopyRules} variant="outline" size="sm">
                      <Copy className="h-4 w-4 mr-2" />
                      Copy from...
                    </Button>
                    <Button onClick={handleAddRule} size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Rule
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-2xl font-bold">{selectedProviderRules.length}</div>
                      <p className="text-xs text-muted-foreground">Total Rules</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-2xl font-bold text-green-600">
                        {selectedProviderRules.filter((r) => r.ruleType === 'ALLOW').length}
                      </div>
                      <p className="text-xs text-muted-foreground">ALLOW Rules</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-2xl font-bold text-red-600">
                        {selectedProviderRules.filter((r) => r.ruleType === 'DENY').length}
                      </div>
                      <p className="text-xs text-muted-foreground">DENY Rules</p>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>

            {/* Rules Content */}
            <ProviderRulesPanel
              provider={{
                id: selectedProvider?.id || '',
                name: selectedProvider?.name || '',
              }}
              rules={selectedProviderRules}
              isLoading={rulesQuery.isLoading}
              onAddRule={handleAddRule}
              onEditRule={handleEditRule}
              onDeleteRule={handleDeleteRule}
              onCopyRules={handleCopyRules}
            />
          </div>
        )
      }
    >
      {/* Dialogs */}
      <EditProviderRuleDialog
        rule={editingRule}
        open={!!editingRuleId || showAddRuleDialog}
        onOpenChange={(open) => {
          if (!open) {
            setEditingRuleId(null);
            setShowAddRuleDialog(false);
          }
        }}
        onSave={handleRefresh}
        organizationId={currentOrganizationId || ''}
        ehrSystem={selectedEhrSystem}
        providerId={selectedProviderId || undefined}
        providerName={selectedProvider?.name || undefined}
      />

      <CopyProviderRulesDialog
        open={showCopyDialog}
        onOpenChange={setShowCopyDialog}
        targetProvider={selectedProvider}
        allProviders={providersQuery.data?.providers || []}
        organizationId={currentOrganizationId || ''}
        ehrSystem={selectedEhrSystem}
        onSuccess={handleCopySuccess}
      />

      <AlertDialog open={!!deletingRuleId} onOpenChange={() => setDeletingRuleId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Provider Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this rule? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminTwoPanelLayout>
  );
}
